import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight } from 'lucide-react';
import { JobsShell } from '../components/jobs/JobsShell';
import { useJobseekerAuth, formatApiError } from '../contexts/JobseekerAuthContext';

export default function JobsLoginPage() {
  const navigate = useNavigate();
  const { login } = useJobseekerAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/jobs/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <JobsShell showSigninLink={false}>
      <div className="max-w-md mx-auto px-4 sm:px-6 py-14" data-testid="jobs-login-page">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <h1 className="text-2xl font-extrabold text-slate-900">Welcome back</h1>
          <p className="text-slate-600 text-sm mt-1">Sign in to your DentalStaff profile.</p>

          {error && (
            <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-base"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-700">Password</label>
                <Link to="/jobs/forgot-password" className="text-xs text-blue-700 hover:underline font-semibold" data-testid="login-forgot-link">
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-base"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full px-5 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 disabled:opacity-60 text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            New here?{' '}
            <Link to="/jobs/signup" className="text-blue-700 font-bold hover:underline" data-testid="login-to-signup-link">
              Create a free account
            </Link>
          </p>
        </div>
      </div>
      <style>{`
        .input-base {
          width: 100%; padding: 10px 12px; border: 2px solid #e2e8f0;
          border-radius: 10px; font-size: 15px; background: #fff;
          transition: border-color .15s ease;
        }
        .input-base:focus {
          outline: none; border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
        }
      `}</style>
    </JobsShell>
  );
}
